import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api, { bootstrapAccessToken, clearAuthStorage, setAuthTokens } from '../services/api';
import whotVoiceAnnouncer from '../services/whotVoiceAnnouncer';

const AuthContext = createContext(null);

async function applyServerSettings() {
  try {
    const response = await api.profile.getSettings({ skipAuthRefresh: true });
    const s = response.data?.data?.settings;
    if (!s) return;
    whotVoiceAnnouncer.applySettings({ enabled: s.whotAnnouncerEnabled, volume: s.whotAnnouncerVolume });
  } catch (_) { /* settings are non-critical to authentication */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const initialize = useCallback(async () => {
    try {
      const response = await api.profile.getMe({ skipAuthRedirect: true });
      const profile = response.data?.data?.profile || null;
      setUser(profile ? { ...profile, isAdmin: Boolean(profile.isAdmin ?? profile.is_admin) } : null);
      if (profile) {
        await bootstrapAccessToken();
        await applyServerSettings();
      }
    } catch (_) {
      clearAuthStorage();
      setUser(null);
    } finally {
      setInitialized(true);
    }
  }, []);

  useEffect(() => { initialize(); }, [initialize]);
  useEffect(() => {
    const onCleared = () => setUser(null);
    window.addEventListener('9jawin-auth-cleared', onCleared);
    return () => window.removeEventListener('9jawin-auth-cleared', onCleared);
  }, []);

  const completeAuthentication = useCallback(async (data = {}) => {
    // Login/register responses still contain tokens for backward-compatible API
    // clients, but this browser never persists them. The server also sets the
    // HttpOnly cookies that are the durable source of authentication.
    setAuthTokens(data);
    const currentUser = data.user ? { ...data.user, isAdmin: Boolean(data.user.isAdmin ?? data.user.is_admin) } : null;
    setUser(currentUser);
    setInitialized(true);
    if (currentUser) await applyServerSettings();
  }, []);

  const logout = useCallback(async () => {
    try { await api.auth.logout(); } catch (_) { /* server/session may already be gone */ }
    clearAuthStorage();
    setUser(null);
  }, []);

  return <AuthContext.Provider value={{ user, initialized, isAuthenticated: Boolean(user), completeAuthentication, logout, initialize }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider.');
  return context;
}
